import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, UploadCloud, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatINR, resolveImg } from "@/lib/api";
import { toast } from "sonner";

const KARAT_PURITY = { "24K": 1.0, "22K": 0.916, "18K": 0.75, "14K": 0.585, "9K": 0.375 };
const CARAT_TO_GRAM = 0.2; // 1 carat = 0.2 grams
const DIAMOND_SHAPES = ["Round", "Princess", "Oval", "Emerald", "Pear", "Marquise", "Cushion", "Radiant", "Asscher", "Heart", "Baguette"];
const GOLD_COLORS = ["Yellow", "White", "Rose"];
const RING_SIZES = Array.from({ length: 15 }, (_, i) => 8 + i);
const MANGALSUTRA_LENGTHS = [14, 16, 18, 20, 22];
// Curated multi-select picklists for the catalogue taxonomy fields — mirrors the backend's
// STYLE_OPTIONS/OCCASION_OPTIONS constants (server.py) so admin data stays typo-free & consistent.
const STYLE_OPTIONS = ["Classic", "Contemporary", "Minimal", "Statement", "Vintage", "Traditional", "Modern", "Floral", "Geometric"];
const OCCASION_OPTIONS = ["Everyday", "Office", "Party", "Wedding", "Bridal", "Engagement", "Anniversary", "Festive", "Gifting"];
const emptyProduct = {
  name: "", category: "Rings", description: "", metal: "Gold", gemstone_type: "None", images: [],
  gold: { weight_g: 0, gross_weight_g: 0, karat: "22K", color: "Yellow", wastage_pct: 0, making_type: "flat", making_value: 0 },
  diamonds: [], colour_stones: [], certification_charges: 0, hallmarking_charges: 0, stock: 0, featured: false, sku: "",
  default_ring_size: 15, default_mangalsutra_length_in: 18,
  // Catalogue taxonomy (Smart Search groundwork) — additive, all optional
  subcategory: "", jewellery_type: "", style: [], occasion: [], tags: [], search_synonyms: [],
};

// Toggle a value in/out of a multi-select array field (Style / Occasion pills).
function toggleInArray(arr, value) {
  return (arr || []).includes(value) ? arr.filter((v) => v !== value) : [...(arr || []), value];
}

// Comma-separated free-text fields (Tags / Search Synonyms) are edited as plain strings and
// split/trimmed into a clean string array — empty entries (e.g. trailing commas) are dropped.
function csvToArray(text) {
  return (text || "").split(",").map((s) => s.trim()).filter(Boolean);
}

// Label suffix shown next to "Making" based on the selected making type.
function makingLabelSuffix(makingType) {
  if (makingType === "percent") return "%";
  if (makingType === "per_gram") return "(₹/g)";
  return "₹";
}

function diamondRate(matrix, d) {
  const sec = matrix?.[d.diamond_type === "lab" ? "lab" : "natural"];
  if (!sec) return 0;
  return Number(sec.rates?.[`${d.size_id}|${d.grade_id}`] || 0);
}

// Total carat weight of all diamonds + colour stones on the form (used to auto-derive net gold weight).
function totalStoneWeightG(p) {
  const totalCt = (p.diamonds || []).reduce((a, d) => a + (Number(d.carat) || 0), 0)
    + (p.colour_stones || []).reduce((a, s) => a + (Number(s.carat) || 0), 0);
  return totalCt * CARAT_TO_GRAM;
}

// Net (pure metal) weight = Gross Weight - total diamond/stone weight (converted ct -> g).
function computeNetWeight(p) {
  const gross = Number(p.gold?.gross_weight_g) || 0;
  if (gross <= 0) return Number(p.gold?.weight_g) || 0;
  return Math.max(0, gross - totalStoneWeightG(p));
}

// Making charge amount for a given making type (flat ₹ / percent of metal value / per-gram rate).
function computeMakingValue(makingType, makingValue, metal, netWeight) {
  if (makingType === "percent") return (metal * makingValue) / 100;
  if (makingType === "per_gram") return makingValue * netWeight;
  return makingValue;
}

function computePreview(p, rates, matrix) {
  if (!rates) return 0;
  let subtotal = 0;
  const g = p.gold || {};
  const netWeight = computeNetWeight(p);
  if (netWeight > 0) {
    const pf = KARAT_PURITY[g.karat] || 1;
    const metal = netWeight * Number(rates.gold_rate_per_gram_24k) * pf;
    const wastage = metal * (Number(g.wastage_pct) || 0) / 100;
    const makingValue = Number(g.making_value) || 0;
    const making = computeMakingValue(g.making_type, makingValue, metal, netWeight);
    subtotal += metal + wastage + making;
  }
  // `carat` is the TOTAL weight of the line (not per-piece) — count is informational only.
  (p.diamonds || []).forEach((d) => { subtotal += (Number(d.carat) || 0) * diamondRate(matrix, d); });
  (p.colour_stones || []).forEach((s) => { subtotal += (Number(s.carat) || 0) * (rates.stone_rates[s.quality] || 0); });
  // Flat, per-piece B2B charges — always taxed with GST like every other component.
  subtotal += (Number(p.certification_charges) || 0) + (Number(p.hallmarking_charges) || 0);
  return Math.round(subtotal * (1 + (rates.gst_pct || 3) / 100));
}

export function ProductFormDialog({ open, onOpenChange, product, categories, rates, matrix, onSaved }) {
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const withRowKeys = (arr) => (arr || []).map((item) => (item._rowKey ? item : { ...item, _rowKey: crypto.randomUUID() }));

  useEffect(() => {
    if (product) {
      const gold = { ...emptyProduct.gold, ...(product.gold || {}) };
      if (!gold.gross_weight_g) gold.gross_weight_g = gold.weight_g || 0;
      setForm({
        ...emptyProduct, ...product, images: product.images || [], gold,
        diamonds: withRowKeys(product.diamonds), colour_stones: withRowKeys(product.colour_stones),
      });
    } else { setForm(emptyProduct); }
  }, [product, open]);

  const stoneQualities = Object.keys(rates?.stone_rates || {});
  const preview = computePreview(form, rates, matrix);

  const sectionFor = (type) => matrix?.[type === "lab" ? "lab" : "natural"] || { sizes: [], grades: [] };
  const firstOf = (type) => { const sec = sectionFor(type); return { size_id: sec.sizes?.[0]?.id || "", grade_id: sec.grades?.[0]?.id || "" }; };

  const setGold = (k, v) => setForm({ ...form, gold: { ...form.gold, [k]: v } });
  const addDiamond = () => { const f = firstOf("natural"); setForm({ ...form, diamonds: [...form.diamonds, { _rowKey: crypto.randomUUID(), diamond_type: "natural", shape: "Round", ...f, carat: 0, count: 1 }] }); };
  const setDiamond = (i, patch) => { const arr = form.diamonds.map((d, x) => (x === i ? { ...d, ...patch } : d)); setForm({ ...form, diamonds: arr }); };
  const addStone = () => setForm({ ...form, colour_stones: [...form.colour_stones, { _rowKey: crypto.randomUUID(), carat: 0, count: 1, quality: stoneQualities[0] || "" }] });

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (uploaded.length) {
      setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} image(s) uploaded`);
    }
    setUploading(false);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); };
  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    const netWeight = computeNetWeight(form);
    const payload = { ...form, images: form.images || [],
      stock: Number(form.stock), certification_charges: Number(form.certification_charges) || 0, hallmarking_charges: Number(form.hallmarking_charges) || 0,
      gold: { ...form.gold, weight_g: netWeight, gross_weight_g: Number(form.gold.gross_weight_g) || netWeight, wastage_pct: Number(form.gold.wastage_pct), making_value: Number(form.gold.making_value) },
      diamonds: form.diamonds.map((d) => {
        const sec = sectionFor(d.diamond_type);
        const size = (sec.sizes || []).find((s) => s.id === d.size_id);
        const grade = (sec.grades || []).find((g) => g.id === d.grade_id);
        return {
          diamond_type: d.diamond_type || "natural", shape: d.shape || "",
          size_id: d.size_id || "", grade_id: d.grade_id || "",
          size_label: size?.label || "", grade_label: grade?.label || "",
          carat: Number(d.carat), count: Number(d.count), quality: grade?.label || "",
        };
      }),
      colour_stones: form.colour_stones.map(({ _rowKey, ...s }) => ({ ...s, carat: Number(s.carat), count: Number(s.count) })) };
    try {
      if (product) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(product ? "Product updated" : "Product created");
      onSaved(); onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-product-form" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">{product ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <Tabs defaultValue="general">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="materials">Materials</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-3 mt-4">
            <div><Label>Name</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Gemstone Type</Label>
                <Select value={form.gemstone_type} onValueChange={(v) => setForm({ ...form, gemstone_type: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["None", "Diamond", "Ruby", "Emerald", "Sapphire", "Pearl"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>

            {/* Catalogue taxonomy — additive fields for Smart Search groundwork. All optional. */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subcategory</Label>
                <Input data-testid="product-subcategory-input" placeholder="e.g. Engagement Rings" value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="mt-1" />
              </div>
              <div><Label>Jewellery Type</Label>
                <Input data-testid="product-jewellery-type-input" placeholder="e.g. Solitaire Ring, Hoop Earrings" value={form.jewellery_type || ""} onChange={(e) => setForm({ ...form, jewellery_type: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Style</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {STYLE_OPTIONS.map((opt) => {
                  const active = (form.style || []).includes(opt);
                  return (
                    <button key={opt} type="button" data-testid={`product-style-pill-${opt.toLowerCase()}`}
                      onClick={() => setForm({ ...form, style: toggleInArray(form.style, opt) })}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors duration-150 border ${
                        active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]" : "bg-card text-foreground/70 border-border hover:border-[hsl(var(--primary))]"
                      }`}
                    >{opt}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Occasion</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {OCCASION_OPTIONS.map((opt) => {
                  const active = (form.occasion || []).includes(opt);
                  return (
                    <button key={opt} type="button" data-testid={`product-occasion-pill-${opt.toLowerCase()}`}
                      onClick={() => setForm({ ...form, occasion: toggleInArray(form.occasion, opt) })}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors duration-150 border ${
                        active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]" : "bg-card text-foreground/70 border-border hover:border-[hsl(var(--primary))]"
                      }`}
                    >{opt}</button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tags</Label>
                <Input data-testid="product-tags-input" placeholder="comma-separated, e.g. bestseller, gift" value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: csvToArray(e.target.value) })} className="mt-1" />
                <p className="text-[11px] text-muted-foreground mt-1">Comma-separated. Used for catalogue search &amp; recommendations.</p>
              </div>
              <div><Label>Search Synonyms</Label>
                <Input data-testid="product-search-synonyms-input" placeholder="comma-separated, e.g. mangalsutra, thali chain" value={(form.search_synonyms || []).join(", ")} onChange={(e) => setForm({ ...form, search_synonyms: csvToArray(e.target.value) })} className="mt-1" />
                <p className="text-[11px] text-muted-foreground mt-1">Alt search terms only — never shown on the storefront.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Stock <span className="text-[11px] text-muted-foreground font-normal">(derived from itemized pieces)</span></Label>
                <Input data-testid="product-stock-input" type="number" value={product ? (product.stock ?? 0) : 0} readOnly disabled className="mt-1 font-mono" />
                <p className="text-[11px] text-muted-foreground mt-1">{product ? "Add stock from Inventory -> barcode icon -> Add pieces (choose a Source Reference)." : "New designs start at 0 stock (Make to Order) until pieces are added."}</p>
              </div>
              <div className="flex items-center gap-3 mt-6"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /><Label>Featured</Label></div>
            </div>
          </TabsContent>

          <TabsContent value="materials" className="space-y-4 mt-4">
            <div className="rounded-lg border border-border p-4">
              <div className="font-medium mb-3">Gold</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Gross Weight (g)</Label><Input data-testid="gold-gross-weight-input" type="number" value={form.gold.gross_weight_g} onChange={(e) => setGold("gross_weight_g", e.target.value)} className="mt-1" /><p className="text-[11px] text-muted-foreground mt-1">As-weighed weight of the finished piece (gold + stones)</p></div>
                <div><Label>Net Weight (g)</Label><Input data-testid="gold-weight-input" type="number" value={computeNetWeight(form).toFixed(3)} disabled className="mt-1 bg-[#F4EBDD] text-muted-foreground" /><p className="text-[11px] text-muted-foreground mt-1">Auto = Gross − Diamond/Stone weight (1ct = 0.2g). Used for pricing.</p></div>
              </div>
              <div className="mt-3"><Label>Karat</Label><Select value={form.gold.karat} onValueChange={(v) => setGold("karat", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(KARAT_PURITY).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></div>
              <div className="mt-3">
                <Label>Gold Color</Label>
                <p className="text-[11px] text-muted-foreground mb-1">Cosmetic only — shown as a "Customize Your Piece" option on the storefront, no price impact.</p>
                <Select value={form.gold.color || "Yellow"} onValueChange={(v) => setGold("color", v)}>
                  <SelectTrigger data-testid="gold-color-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{GOLD_COLORS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.category === "Rings" && (
                <div className="mt-3">
                  <Label>Default (In-Stock) Ring Size</Label>
                  <p className="text-[11px] text-muted-foreground mb-1">The size THIS physical piece was made at. Any other size a shopper selects becomes a "Request Custom Build".</p>
                  <Select value={String(form.default_ring_size || 15)} onValueChange={(v) => setForm({ ...form, default_ring_size: Number(v) })}>
                    <SelectTrigger data-testid="default-ring-size-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{RING_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.category === "Mangalsutra" && (
                <div className="mt-3">
                  <Label>Default (In-Stock) Length</Label>
                  <p className="text-[11px] text-muted-foreground mb-1">The length THIS physical piece was made at. Any other length a shopper selects becomes a "Request Custom Build".</p>
                  <Select value={String(form.default_mangalsutra_length_in || 18)} onValueChange={(v) => setForm({ ...form, default_mangalsutra_length_in: Number(v) })}>
                    <SelectTrigger data-testid="default-mangalsutra-length-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{MANGALSUTRA_LENGTHS.map((l) => <SelectItem key={l} value={String(l)}>{l}"</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3"><div className="font-medium">Diamonds</div><Button size="sm" variant="secondary" onClick={addDiamond} className="gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button></div>
              {(!matrix || ((matrix.natural?.grades?.length || 0) + (matrix.lab?.grades?.length || 0) === 0)) && (
                <p className="text-xs text-muted-foreground mb-2">No diamond grades defined yet. Add sizes &amp; grades in <span className="font-medium">Diamond Rates</span> first.</p>
              )}
              {form.diamonds.map((d, i) => {
                const sec = sectionFor(d.diamond_type);
                const rate = diamondRate(matrix, d);
                const lineValue = Math.round((Number(d.carat) || 0) * rate);
                return (
                  <div key={d._rowKey || i} data-testid="diamond-row" className="rounded-lg border border-border bg-[#F7F0E6] p-3 mb-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div><Label className="text-xs">Type</Label>
                        <Select value={d.diamond_type || "natural"} onValueChange={(v) => setDiamond(i, { diamond_type: v, ...firstOf(v) })}>
                          <SelectTrigger data-testid="diamond-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="natural">Natural</SelectItem><SelectItem value="lab">Lab-Grown</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Shape</Label>
                        <Select value={d.shape || "Round"} onValueChange={(v) => setDiamond(i, { shape: v })}>
                          <SelectTrigger data-testid="diamond-shape-select" className="mt-1"><SelectValue placeholder="Select shape" /></SelectTrigger>
                          <SelectContent>{DIAMOND_SHAPES.map((sh) => <SelectItem key={sh} value={sh}>{sh}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Size</Label>
                        <Select value={d.size_id || ""} onValueChange={(v) => setDiamond(i, { size_id: v })}>
                          <SelectTrigger data-testid="diamond-size-select" className="mt-1"><SelectValue placeholder="Select size" /></SelectTrigger>
                          <SelectContent>{(sec.sizes || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Grade (Colour + Clarity)</Label>
                        <Select value={d.grade_id || ""} onValueChange={(v) => setDiamond(i, { grade_id: v })}>
                          <SelectTrigger data-testid="diamond-grade-select" className="mt-1"><SelectValue placeholder="Select grade" /></SelectTrigger>
                          <SelectContent>{(sec.grades || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-[1.2fr_1fr_1.4fr_auto] gap-2 items-end mt-2">
                      <div><Label className="text-xs">Total Weight (ct)</Label><Input data-testid="diamond-carat-input" type="number" value={d.carat} onChange={(e) => setDiamond(i, { carat: e.target.value })} className="mt-1" /><p className="text-[10px] text-muted-foreground mt-1">Total weight of this line, not per-piece</p></div>
                      <div><Label className="text-xs">Pieces</Label><Input data-testid="diamond-count-input" type="number" value={d.count} onChange={(e) => setDiamond(i, { count: e.target.value })} className="mt-1" /><p className="text-[10px] text-muted-foreground mt-1">For reference only</p></div>
                      <div>
                        <Label className="text-xs">Auto rate / value</Label>
                        <div className="mt-1 h-9 flex items-center px-2 rounded-md bg-card border border-border text-xs font-mono">
                          <span className={rate > 0 ? "text-foreground" : "text-destructive"}>{formatINR(rate)}/ct</span>
                          <span className="mx-1 text-muted-foreground">→</span>
                          <span className="text-[hsl(var(--primary))]">{formatINR(lineValue)}</span>
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, diamonds: form.diamonds.filter((_, x) => x !== i) })}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    {d.size_id && d.grade_id && rate === 0 && (
                      <p className="text-[11px] text-destructive mt-1.5">No rate set for this Size × Grade in Diamond Rates — this diamond adds ₹0.</p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-3"><div className="font-medium">Colour Stones</div><Button size="sm" variant="secondary" onClick={addStone} className="gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button></div>
              {form.colour_stones.map((s, i) => (
                <div key={s._rowKey || i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2 items-end">
                  <div><Label className="text-xs">Type</Label><Select value={s.quality} onValueChange={(v) => { const arr = [...form.colour_stones]; arr[i].quality = v; setForm({ ...form, colour_stones: arr }); }}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{stoneQualities.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-xs">Total Weight (ct)</Label><Input type="number" value={s.carat} onChange={(e) => { const arr = [...form.colour_stones]; arr[i].carat = e.target.value; setForm({ ...form, colour_stones: arr }); }} className="mt-1" /></div>
                  <div><Label className="text-xs">Pieces</Label><Input type="number" value={s.count} onChange={(e) => { const arr = [...form.colour_stones]; arr[i].count = e.target.value; setForm({ ...form, colour_stones: arr }); }} className="mt-1" /></div>
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, colour_stones: form.colour_stones.filter((_, x) => x !== i) })}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Wastage %</Label><Input type="number" value={form.gold.wastage_pct} onChange={(e) => setGold("wastage_pct", e.target.value)} className="mt-1" /></div>
              <div><Label>Making Type</Label>
                <Select value={form.gold.making_type} onValueChange={(v) => setGold("making_type", v)}>
                  <SelectTrigger data-testid="product-making-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat (₹)</SelectItem>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                    <SelectItem value="per_gram">Per Gram (₹/g)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Making {makingLabelSuffix(form.gold.making_type)}</Label><Input data-testid="product-making-value-input" type="number" value={form.gold.making_value} onChange={(e) => setGold("making_value", e.target.value)} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Certification Charges (₹)</Label><Input data-testid="product-certification-charges-input" type="number" value={form.certification_charges} onChange={(e) => setForm({ ...form, certification_charges: e.target.value })} className="mt-1" /></div>
              <div><Label>Hallmarking Charges (₹)</Label><Input data-testid="product-hallmarking-charges-input" type="number" value={form.hallmarking_charges} onChange={(e) => setForm({ ...form, hallmarking_charges: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="rounded-lg border border-border bg-[#F7F0E6] p-4 mt-2">
              <div className="text-xs text-muted-foreground">Live Price Preview (incl. GST at current rates)</div>
              <div data-testid="product-price-preview" className="font-mono text-2xl text-[hsl(var(--primary))] mt-1">{formatINR(preview)}</div>
              <div className="text-xs text-muted-foreground mt-1">Gold rate: {formatINR(rates?.gold_rate_per_gram_24k)}/g (24K)</div>
            </div>
          </TabsContent>

          <TabsContent value="media" className="space-y-3 mt-4">
            <Label>Product Images</Label>
            <div
              data-testid="product-image-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`mt-1 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-[border-color,background-color] ${dragOver ? "border-[hsl(var(--primary))] bg-[#F7E9EC]" : "border-border bg-[#F7F0E6]"}`}
            >
              <input ref={fileInputRef} data-testid="product-image-file-input" type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
              {uploading ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /> Uploading...</div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <UploadCloud className="h-8 w-8 text-[hsl(var(--primary))]" />
                  <div className="text-sm font-medium text-foreground">Drag & drop images here, or click to browse</div>
                  <div className="text-xs">JPG, PNG, WEBP or GIF · up to 8MB each</div>
                </div>
              )}
            </div>
            {(form.images || []).length > 0 && (
              <div className="grid grid-cols-4 gap-3 mt-2">
                {form.images.map((u, i) => (
                  <div key={u} data-testid="product-image-thumb" className="relative group rounded-lg overflow-hidden border border-border">
                    <img src={resolveImg(u)} alt="" className="h-24 w-full object-cover" />
                    <button type="button" data-testid="product-image-remove" onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3.5 w-3.5" />
                    </button>
                    {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] bg-[hsl(var(--primary))] text-white px-1.5 py-0.5 rounded">Cover</span>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="product-save-button" onClick={save} disabled={saving || !form.name}>{saving ? "Saving..." : "Save Product"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
